import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency, cn } from "@/lib/utils";
import { SHARE_VALUE, calculateShares } from "@/utils/shares";
import {
  Users, User, Search, RefreshCw, AlertTriangle, CheckCircle2,
  Clock, CreditCard, PiggyBank, Wallet, FileText, Phone, Mail,
  Calendar, BadgeCheck, Banknote, AlertOctagon, Send, ArrowLeft,
  TrendingUp, Hash, CircleUserRound, Layers, UserCheck, ChevronDown,
  X, Save, HelpCircle, TrendingDown, CircleDollarSign, ChevronRight,
  Keyboard, History, Heart, Shield, Activity, DollarSign, Repeat,
  Stethoscope, BarChart3, Download, Printer, Eye, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import type { ColumnDef } from "@tanstack/react-table";

/* ──────────────── Types ──────────────── */

interface MemberSearchResult {
  id: number;
  memberCode: string;
  fullNames: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  membershipStatus: string;
  joinedAt: string;
  groupId: number;
  totalShares: number;
  totalContributions: number;
  activeLoan: boolean;
}

interface DeclarationProfile {
  personalInfo: {
    id: number;
    memberCode: string;
    fullName: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    group: string;
    branch: string;
    status: string;
    joinedAt: string;
  };
  contributionSummary: {
    totalContributions: number;
    totalContributionsCount: number;
    totalShares: number;
    previousContribution: number;
    minimumMonthlyContribution: number;
    contributionFineAccumulation: number;
    shareValue: number;
  };
  loanSummary: {
    loanId: number;
    loanCode: string;
    totalGiven: number;
    totalPaid: number;
    loanBalance: number;
    outstandingBalance: number;
    monthlyInstallment: number;
    nextDueDate: string;
    status: string;
  } | null;
  fines: {
    pendingFines: number;
    loanFineAccumulation: number;
    contributionFineAccumulation: number;
  };
  settings: {
    minimumMonthlyContribution: number;
    shareValue: number;
    month: number;
    year: number;
    monthName: string;
  };
  existingDeclaration: {
    id: number;
    declaredAmount: number;
    status: string;
    payrollStatus: string;
  } | null;
}

interface FinancialWorkspace {
  member: {
    id: number;
    memberCode: string;
    fullName: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    email: string | null;
    status: string;
    joinedAt: string;
    memberSince: number;
    totalShares: number;
    groupName: string | null;
  };
  contributionSummary: {
    totalContributions: number;
    totalContributionsCount: number;
    totalShares: number;
    previousContribution: number;
    minimumMonthlyContribution: number;
    shareValue: number;
    contributionFineAccumulation: number;
    contributionPeriod: { from: string; to: string } | null;
    lastContributionDate: string | null;
    currentMonthStatus: string;
  };
  loanSummary: {
    loanId: number;
    loanCode: string;
    totalGiven: number;
    totalPaid: number;
    outstandingBalance: number;
    monthlyInstallment: number;
    nextDueDate: string;
    status: string;
    loanBalance: number;
  } | null;
  loansSummary: {
    hasActiveLoan: boolean;
    totalLoanCount: number;
    paidLoanCount: number;
    totalLoanGiven: number;
    totalLoanPaid: number;
    totalLoanOutstanding: number;
    loanFineAccumulation: number;
  };
  fines: {
    pendingFines: number;
    loanFineAccumulation: number;
    contributionFineAccumulation: number;
  };
  accountHealth: {
    contributionRegularity: string;
    loanRiskLevel: string;
    outstandingFines: number;
    daysSinceLastContribution: number | null;
    monthsSaved: number;
  };
  missingContributions: {
    months: Array<{
      month: number;
      year: number;
      monthName: string;
      requiredContribution: number;
      fineAmount: number;
      totalDue: number;
      status: string;
      dueDate: string;
      declarationId: number | null;
    }>;
    summary: {
      missingMonthsCount: number;
      missingContributionsTotal: number;
      missingFinesTotal: number;
      missingDueTotal: number;
    };
  };
  recentTransactions: Array<{
    date: string;
    type: string;
    description: string;
    amount: number;
    method: string | null;
    status: string;
  }>;
  settings: {
    minimumMonthlyContribution: number;
    shareValue: number;
    month: number;
    year: number;
    monthName: string;
  };
  existingDeclaration: {
    id: number;
    declaredAmount: number;
    status: string;
    payrollStatus: string;
    approvalCount: number;
    requiredApprovals: number;
  } | null;
}

interface MonthlySavingsHistory {
  summary: {
    monthsExpected: number;
    monthsSaved: number;
    missedMonths: number;
    totalFines: number;
    savingsConsistencyPercent: number;
  };
  months: Array<{
    month: number;
    year: number;
    monthName: string;
    requiredContribution: number;
    declaredContribution: number;
    sharesPurchased: number;
    paymentMethod: string | null;
    paymentDate: string | null;
    status: string;
    fineAmount: number;
    approvedBy: string | null;
    approvalProgress: string;
    declarationId: number | null;
  }>;
}

type DeclarationMode = "single" | "multiple" | "all";
type SettlementTab = "contribution" | "loan" | "fine" | "mixed";

/* ──────────────── Constants ──────────────── */

const PAYMENT_METHODS = ["Salary Deduction", "Cash", "Mobile Money", "Bank Transfer"];

/* ──────────────── Helpers ──────────────── */

function highlightMatch(text: string, query: string) {
  if (!query || !text) return text;
  const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  return text.split(re).map((p, i) =>
    re.test(p) ? <mark key={i} className="bg-yellow-100 text-yellow-800 rounded px-0.5">{p}</mark> : p
  );
}
function getInitials(firstName?: string, lastName?: string) {
  return ((firstName?.[0] || "") + (lastName?.[0] || "")).toUpperCase() || "?";
}

const AVATAR_COLORS = [
  "bg-emerald-500", "bg-blue-500", "bg-violet-500", "bg-amber-500",
  "bg-rose-500", "bg-cyan-500", "bg-indigo-500", "bg-pink-500",
  "bg-teal-500", "bg-orange-500",
];
function getAvatarColor(id: number) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

const normalize = (value?: string | number | null) =>
  String(value ?? "").toLowerCase().trim().replace(/\s+/g, " ");

function rankMemberSearch(member: any, rawQuery: string): number {
  const query = normalize(rawQuery);
  if (!query) return 0;
  const name = normalize(member.memberName || member.fullNames);
  const code = normalize(member.memberCode);
  const phone = normalize(member.phone);
  const nationalId = normalize(member.nationalId);
  const words = name.split(" ");
  if (name === query) return 1;
  if (name.startsWith(query)) return 2;
  if (words.some((word: string) => word.startsWith(query))) return 3;
  if (code.startsWith(query)) return 4;
  if (phone.startsWith(query)) return 5;
  if (nationalId.startsWith(query)) return 6;
  if (name.includes(query)) return 7;
  if (code.includes(query) || phone.includes(query) || nationalId.includes(query)) return 8;
  return 999;
}

const formatSharesAmount = (amount: number) => {
  const shares = amount / SHARE_VALUE;
  return Number.isInteger(shares) ? shares.toString() : shares.toFixed(2).replace(/\.?0+$/, "");
};

/* ──────────────── Status Filter Tabs (shared) ──────────────── */

type StatusFilter = "all" | "pending" | "declared" | "overdue";

interface StatusFilterTabsProps {
  activeFilter: StatusFilter;
  onFilterChange: (filter: StatusFilter) => void;
  counts: { all: number; pending: number; declared: number; overdue: number };
  compact?: boolean;
}

function StatusFilterTabs({ activeFilter, onFilterChange, counts, compact }: StatusFilterTabsProps) {
  const tabs: { key: StatusFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all },
    { key: "pending", label: "Pending", count: counts.pending },
    { key: "declared", label: "Declared", count: counts.declared },
    { key: "overdue", label: "Overdue", count: counts.overdue },
  ];
  return (
    <div className={cn("flex items-center gap-1 bg-white rounded-xl border border-gray-200 p-1 shadow-sm", compact && "rounded-lg")}>
      {tabs.map(({ key, label, count }) => (
        <button key={key} onClick={() => onFilterChange(key)}
          className={cn("flex items-center gap-1.5 rounded-lg text-[12px] font-medium transition-all", compact ? "px-2.5 py-1" : "px-3 py-1.5",
            activeFilter === key ? "bg-[#009A44] text-white shadow-sm" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          )}>
          {label}
          <span className={cn("inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold px-1",
            activeFilter === key ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
          )}>{count}</span>
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
/*                    MAIN COMPONENT                         */
/* ══════════════════════════════════════════════════════════ */

export default function DeclareMonthlyShares() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const singleListRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLButtonElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  // ── State ──
  const [mode, setMode] = useState<DeclarationMode>("single");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  // ── Settlement state ──
  const [settlementTab, setSettlementTab] = useState<SettlementTab>("contribution");
  const [contributionAmount, setContributionAmount] = useState("");
  const [loanRepaymentAmount, setLoanRepaymentAmount] = useState("");
  const [fineAmount, setFineAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Salary Deduction");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // ── Bulk mode state ──
  const [bulkStatusFilter, setBulkStatusFilter] = useState<"all" | "pending" | "declared" | "overdue">("all");
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAmount, setBulkAmount] = useState("");
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [bulkOffset, setBulkOffset] = useState(0);
  const BULK_PAGE_SIZE = 20;

  // ── Recent Transactions DataTable state ──
  const [txnSearch, setTxnSearch] = useState("");
  const [txnSorting, setTxnSorting] = useState<any>([]);
  const [txnColumnFilters, setTxnColumnFilters] = useState<any>([]);
  const [txnPagination, setTxnPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // ── Pending declaration handling ──
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [declarationMode, setDeclarationMode] = useState<"new" | "add_to_existing">("new");
  const [pendingDeclarationId, setPendingDeclarationId] = useState<number | null>(null);

  // ── Preview modal ──
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // ── Recent Transactions column definitions ──
  const txnColumns: ColumnDef<any, any>[] = useMemo(() => [
    {
      accessorKey: "date",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => {
        const d = row.getValue("date");
        return <span className="text-[11px] text-gray-600 whitespace-nowrap">{d ? new Date(d as string).toLocaleDateString() : "—"}</span>;
      },
    },
    {
      accessorKey: "type",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => {
        const type = row.getValue("type") as string;
        return (
          <span className={cn(
            "inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold capitalize",
            type === "contribution" ? "bg-emerald-50 text-emerald-700" :
            type === "loan_payment" ? "bg-blue-50 text-blue-700" :
            type === "fine_payment" ? "bg-amber-50 text-amber-700" :
            "bg-gray-100 text-gray-600"
          )}>
            {(type || "N/A").replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      accessorKey: "description",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
      cell: ({ row }) => {
        return <span className="text-[11px] text-gray-700 truncate max-w-[200px] block">{row.getValue("description") || "—"}</span>;
      },
    },
    {
      accessorKey: "amount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
      cell: ({ row }) => {
        const amount = row.getValue("amount") as number;
        const type = row.original?.type as string;
        const isNegative = amount < 0 || type === "fine" || type === "penalty" || type === "loan";
        return (
          <span className={cn(
            "text-[12px] font-semibold whitespace-nowrap",
            isNegative ? "text-red-600" : "text-emerald-600"
          )}>
            {formatCurrency(amount)}
          </span>
        );
      },
    },
    {
      accessorKey: "method",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Method" />,
      cell: ({ row }) => {
        return <span className="text-[11px] text-gray-500 capitalize">{row.getValue("method") || "—"}</span>;
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const s = ((row.getValue("status") as string) || "").toLowerCase();
        let badge = <span className="inline-flex items-center rounded-full bg-gray-50 text-gray-600 border border-gray-200 px-1.5 py-0.5 text-[9px] font-semibold capitalize">{(row.getValue("status") as string) || "N/A"}</span>;
        if (s.includes("approved") || s.includes("completed") || s.includes("verified") || s.includes("paid")) {
          badge = <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 text-[9px] font-semibold">Completed</span>;
        } else if (s.includes("pending") || s.includes("awaiting")) {
          badge = <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[9px] font-semibold">Pending</span>;
        } else if (s.includes("rejected") || s.includes("failed")) {
          badge = <span className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 text-[9px] font-semibold">Rejected</span>;
        }
        return badge;
      },
    },
  ], []);

  // ── Debounce search ──
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ── Unified member list (shared between single & bulk modes) ──
  const { data: memberData, isLoading: membersLoading, error: membersError, refetch: refetchMembers, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    ["declaration-members", selectedMonth, selectedYear, bulkStatusFilter, debouncedQuery],
    async ({ pageParam = 0 }) => {
      const { data } = await api.get("/contributions/bulk-declaration-members", {
        params: { month: selectedMonth, year: selectedYear, status: bulkStatusFilter, q: debouncedQuery, offset: pageParam, limit: BULK_PAGE_SIZE },
      });
      return data?.data;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage?.hasMore) return undefined;
        return allPages.length * BULK_PAGE_SIZE;
      },
      retry: 2,
      refetchOnWindowFocus: false,
    }
  );

  const members = useMemo(() => {
    if (!memberData?.pages) return [];
    return memberData.pages.flatMap((p: any) => p.members || []);
  }, [memberData]);
  const counts = memberData?.pages?.[0]?.counts || { all: 0, pending: 0, declared: 0, overdue: 0 };
  const totalMembers = memberData?.pages?.[0]?.total || 0;
  const hasMore = memberData?.pages?.[memberData.pages.length - 1]?.hasMore ?? false;

  // ── Client-side ranked + filtered member list (single mode search dropdown) ──
  const displayMembers = useMemo(() => {
    const hasQuery = !!debouncedQuery.trim();
    return members
      .map((m: any) => ({ ...m, searchRank: rankMemberSearch(m, debouncedQuery) }))
      .filter((m: any) => !hasQuery || m.searchRank !== 999)
      .sort((a: any, b: any) => {
        if (a.searchRank !== b.searchRank) return a.searchRank - b.searchRank;
        return normalize(a.memberName).localeCompare(normalize(b.memberName));
      });
  }, [members, debouncedQuery]);

  // ── Financial Workspace Query (single member) ──
  const { data: workspace, isLoading: workspaceLoading, error: workspaceError, refetch: refetchWorkspace } = useQuery(
    ["financial-workspace", selectedMemberId, selectedMonth, selectedYear],
    async () => {
      if (!selectedMemberId) return null;
      console.log("[DeclareMonthlyShares] Fetching financial workspace for memberId:", selectedMemberId);
      const { data } = await api.get(`/members/${selectedMemberId}/financial-workspace`);
      console.log("[DeclareMonthlyShares] Workspace response:", data?.success ? "OK" : "FAILED", data?.data?.member?.fullName || "");
      return data?.data as FinancialWorkspace | null;
    },
    {
      enabled: !!selectedMemberId && mode === "single",
      retry: 1,
      onError: (err: any) => {
        console.error("[DeclareMonthlyShares] Workspace fetch error:", err?.response?.status, err?.response?.data?.message || err.message);
      },
    }
  );

  // ── Monthly Savings History Query ──
  const [historyYear, setHistoryYear] = useState(new Date().getFullYear());
  const [historyStatusFilter, setHistoryStatusFilter] = useState<string>("all");
  const [historyMethodFilter, setHistoryMethodFilter] = useState<string>("all");

  const { data: savingsHistory, isLoading: savingsHistoryLoading } = useQuery(
    ["monthly-savings-history", selectedMemberId, historyYear],
    async () => {
      if (!selectedMemberId) return null;
      const { data } = await api.get(`/members/${selectedMemberId}/monthly-savings-history`, {
        params: { year: historyYear },
      });
      return data?.data as MonthlySavingsHistory | null;
    },
    { enabled: !!selectedMemberId && mode === "single" }
  );

  // ── Filtered months ──
  const filteredHistoryMonths = useMemo(() => {
    if (!savingsHistory?.months) return [];
    return savingsHistory.months.filter((m) => {
      if (historyStatusFilter !== "all" && m.status !== historyStatusFilter) return false;
      if (historyMethodFilter !== "all" && m.paymentMethod !== historyMethodFilter) return false;
      return true;
    });
  }, [savingsHistory, historyStatusFilter, historyMethodFilter]);

  // ── Legacy profile query (for bulk/all modes) ──
  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery(
    ["declaration-profile", selectedMemberId, selectedMonth, selectedYear],
    async () => {
      if (!selectedMemberId) return null;
      const { data } = await api.get(`/contributions/declaration-profile/${selectedMemberId}`, {
        params: { month: selectedMonth, year: selectedYear },
      });
      return data?.data as DeclarationProfile | null;
    },
    { enabled: !!selectedMemberId && mode !== "single" }
  );

  // ── Auto-fill settlement amounts when workspace loads ──
  useEffect(() => {
    if (workspace) {
      setContributionAmount(String(workspace.contributionSummary.minimumMonthlyContribution));
      setLoanRepaymentAmount("");
      setFineAmount(String(workspace.fines.contributionFineAccumulation || 0));
      setSettlementTab(workspace.loansSummary.hasActiveLoan ? "contribution" : "contribution");
    }
  }, [workspace]);

  // ── For "all" mode: auto-select all pending members ──
  useEffect(() => {
    if (mode === "all" && members.length > 0) {
      const pendingIds = members.filter((m: any) => m.canDeclare).map((m: any) => m.memberId);
      setBulkSelectedIds(new Set(pendingIds));
    }
  }, [mode, members, bulkStatusFilter]);

  // Reset bulk selection when filters change
  useEffect(() => {
    setBulkSelectedIds(new Set());
    setBulkOffset(0);
  }, [bulkStatusFilter, selectedMonth, selectedYear]);

  // ── Detect pending declaration when a NEW member is selected ──
  useEffect(() => {
    if (!selectedMemberId) {
      setShowPendingDialog(false);
      setDeclarationMode("new");
      setPendingDeclarationId(null);
      return;
    }
    // Always close search dropdown when member is selected
    setSearchFocused(false);
    setSearchQuery("");
    if (workspace?.existingDeclaration && workspace.existingDeclaration.status === "pending_leader_approval") {
      setShowPendingDialog(true);
      setDeclarationMode("new");
      setPendingDeclarationId(null);
    } else {
      setShowPendingDialog(false);
    }
  }, [selectedMemberId]);

  // ── Submit mutation (settlement) ──
  const submitMutation = useMutation(
    async () => {
      if (!selectedMemberId) throw new Error("No member selected");
      const contrib = settlementTab === "contribution" || settlementTab === "mixed" ? Number(contributionAmount) || 0 : 0;
      const loan = settlementTab === "loan" || settlementTab === "mixed" ? Number(loanRepaymentAmount) || 0 : 0;
      const fine = settlementTab === "fine" || settlementTab === "mixed" ? Number(fineAmount) || 0 : 0;
      const total = contrib + loan + fine;

      const { data } = await api.post("/member-settlements", {
        memberId: selectedMemberId,
        settlementType: settlementTab,
        contributionAmount: contrib,
        loanRepaymentAmount: loan,
        finePaymentAmount: fine,
        totalAmount: total,
        paymentMethod,
        referenceNumber: referenceNumber || undefined,
        paymentDate,
        notes: notes || undefined,
        month: selectedMonth - 1,
        year: selectedYear,
        mode: declarationMode,
        existingDeclarationId: declarationMode === "add_to_existing"
          ? pendingDeclarationId
          : undefined,
      });
      return data;
    },
    {
      onSuccess: (data) => {
        toast.success(data?.message || "Declaration submitted successfully and is awaiting approval.");
        queryClient.invalidateQueries("financial-workspace");
        queryClient.invalidateQueries("declaration-profile");
        queryClient.invalidateQueries("declaration-members");
        queryClient.invalidateQueries("monthly-declaration-overview");
        queryClient.invalidateQueries("monthly-savings-history");
        queryClient.invalidateQueries("contributions");
        setContributionAmount("");
        setLoanRepaymentAmount("");
        setFineAmount("");
        setReferenceNumber("");
        setNotes("");
        setDeclarationMode("new");
        setPendingDeclarationId(null);
        setShowPreviewModal(false);
      },
      onError: (err: any) => { toast.error(err?.response?.data?.message || "Failed to process payment"); },
    }
  );

  // ── Bulk submit mutation ──
  const bulkSubmitMutation = useMutation(
    async () => {
      const memberIds = Array.from(bulkSelectedIds);
      if (memberIds.length === 0) throw new Error("No members selected");
      const amount = Number(bulkAmount);
      if (!amount || amount <= 0) throw new Error("Please enter a valid declaration amount");
      const { data } = await api.post("/monthly-saving-declarations/bulk-declare", {
        month: selectedMonth, year: selectedYear, declaredAmount: amount, memberIds,
      });
      return data;
    },
    {
      onSuccess: (data) => {
        const count = data?.data?.filter((r: any) => r.success).length || 0;
        toast.success(`${count} declaration(s) submitted for approval`);
        setBulkSelectedIds(new Set());
        setBulkAmount("");
        setShowBulkModal(false);
        queryClient.invalidateQueries("bulk-declaration-members");
        queryClient.invalidateQueries("monthly-declaration-overview");
        queryClient.invalidateQueries("declaration-profile");
        queryClient.invalidateQueries("contributions");
      },
      onError: (err: any) => { toast.error(err?.response?.data?.message || "Failed to submit bulk declarations"); },
    }
  );

  // ── Handlers ──
  const handleMemberSelect = useCallback((member: any) => {
    console.log("[DeclareMonthlyShares] Member selected:", { memberId: member.memberId, memberCode: member.memberCode, name: member.memberName });
    setSelectedMemberId(member.memberId);
    setSearchQuery("");
    setSearchFocused(false);
    setActiveIndex(-1);
    setContributionAmount("");
    setLoanRepaymentAmount("");
    setFineAmount("");
    setReferenceNumber("");
    setNotes("");
    setSettlementTab("contribution");
    searchInputRef.current?.blur();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMemberId(null);
    setSearchQuery("");
    setContributionAmount("");
    setLoanRepaymentAmount("");
    setFineAmount("");
    setReferenceNumber("");
    setNotes("");
    setDeclarationMode("new");
    setPendingDeclarationId(null);
    setShowPendingDialog(false);
    setShowPreviewModal(false);
    searchInputRef.current?.focus();
  }, []);

  const settleMissingMonth = useCallback((month: number, year: number, requiredContribution: number, fineAmount: number) => {
    setSettlementTab("mixed");
    setContributionAmount(String(requiredContribution));
    setFineAmount(String(fineAmount));
    setSelectedMonth(month + 1);
    setSelectedYear(year);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Settling ${new Date(year, month).toLocaleString("default", { month: "long" })} ${year} — ${formatCurrency(requiredContribution + fineAmount)}`);
  }, []);

  const toggleBulkMember = useCallback((memberId: number) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    const declarable = members.filter((m: any) => m.canDeclare).map((m: any) => m.memberId);
    const allSelected = declarable.length > 0 && declarable.every((id: number) => bulkSelectedIds.has(id));
    setBulkSelectedIds(allSelected ? new Set() : new Set(declarable));
  }, [members, bulkSelectedIds]);

  // ── Keyboard navigation ──
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = Math.min(prev + 1, displayMembers.length - 1);
        if (next >= 0 && singleListRef.current) {
          const items = singleListRef.current.querySelectorAll("[data-member-row]");
          (items[next] as HTMLElement)?.scrollIntoView({ block: "nearest" });
        }
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0 && activeIndex < displayMembers.length) {
      e.preventDefault();
      handleMemberSelect(displayMembers[activeIndex]);
    } else if (e.key === "Escape") {
      setSearchQuery("");
      setActiveIndex(-1);
      setSearchFocused(false);
      searchInputRef.current?.blur();
    }
  }, [displayMembers, activeIndex, handleMemberSelect]);

  // ── Global Ctrl+F shortcut ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── Click outside to close search dropdown ──
  useEffect(() => {
    if (!searchFocused) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-search-dropdown]") && !target.closest("[data-search-input]")) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchFocused]);

  useEffect(() => { setActiveIndex(-1); }, [members]);

  // ── Derived values ──
  const ws = workspace;
  const contributionNum = Number(contributionAmount) || 0;
  const loanRepaymentNum = loanRepaymentAmount ? Number(loanRepaymentAmount) : 0;
  const fineNum = fineAmount ? Number(fineAmount) || 0 : 0;
  const totalPayment = contributionNum + loanRepaymentNum + fineNum;
  const sharesFromContribution = contributionNum > 0 ? formatSharesAmount(contributionNum) : "0";
  const sharesFromContributionNum = Number(sharesFromContribution);
  const minimumAmount = ws?.contributionSummary.minimumMonthlyContribution || 10000;
  const isBelowMinimum = settlementTab !== "loan" && settlementTab !== "fine" && contributionNum > 0 && contributionNum < minimumAmount;
  const needsReference = (paymentMethod === "Bank Transfer" || paymentMethod === "Mobile Money") && totalPayment > 0;
  const missingReference = needsReference && !referenceNumber.trim();
  const canSubmit = totalPayment > 0 && !isBelowMinimum && !!selectedMemberId && !missingReference;

  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  const hasProfile = !!ws;

  // ── Recent Transactions facets ──
  const txnFacets = useMemo(() => {
    const txns = workspace?.recentTransactions || [];
    const types = [...new Set(txns.map((t: any) => t.type))].filter(Boolean);
    const statuses = [...new Set(txns.map((t: any) => t.status))].filter(Boolean);
    const methods = [...new Set(txns.map((t: any) => t.method).filter(Boolean))];
    return [
      { name: "type", title: "Type", type: "select" as const, options: types.map((t: string) => ({ label: t.replace(/_/g, " "), value: t })) },
      { name: "status", title: "Status", type: "select" as const, options: statuses.map((s: string) => ({ label: s, value: s })) },
      { name: "method", title: "Method", type: "select" as const, options: methods.map((m: string) => ({ label: m, value: m })) },
    ];
  }, [workspace?.recentTransactions]);

  // ── Filtered transactions (client-side) ──
  const filteredTxns = useMemo(() => {
    if (!workspace?.recentTransactions) return [];
    let list = [...workspace.recentTransactions];
    if (txnSearch.trim()) {
      const q = txnSearch.toLowerCase();
      list = list.filter((t: any) =>
        (t.date || "").toLowerCase().includes(q) ||
        (t.type || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.method || "").toLowerCase().includes(q) ||
        (t.status || "").toLowerCase().includes(q) ||
        String(t.amount || 0).includes(q)
      );
    }
    txnColumnFilters.forEach((f: any) => {
      if (f.value && f.value.length > 0) {
        list = list.filter((t: any) => f.value.includes(t[f.id]));
      }
    });
    if (txnSorting.length > 0) {
      const { id, desc } = txnSorting[0];
      list.sort((a: any, b: any) => {
        const av = a[id]; const bv = b[id];
        if (av == null) return 1; if (bv == null) return -1;
        const cmp = typeof av === "string" ? av.localeCompare(bv) : Number(av) - Number(bv);
        return desc ? -cmp : cmp;
      });
    }
    return list;
  }, [workspace?.recentTransactions, txnSearch, txnColumnFilters, txnSorting]);

  const txnPageCount = Math.max(1, Math.ceil(filteredTxns.length / txnPagination.pageSize));
  const paginatedTxns = useMemo(() => {
    const start = txnPagination.pageIndex * txnPagination.pageSize;
    return filteredTxns.slice(start, start + txnPagination.pageSize);
  }, [filteredTxns, txnPagination]);

  // ── Export / Print handlers ──
  const handleExportTransactions = useCallback(() => {
    const rows = filteredTxns.map((t: any) => ({
      Date: t.date ? new Date(t.date).toLocaleDateString() : "",
      Type: (t.type || "").replace(/_/g, " "),
      Description: t.description || "",
      Amount: t.amount || 0,
      Method: t.method || "",
      Status: t.status || "",
    }));
    const csv = [Object.keys(rows[0] || {}).join(",")].concat(rows.map(r => Object.values(r).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "recent-transactions.csv"; a.click();
    URL.revokeObjectURL(url);
  }, [filteredTxns]);

  const handlePrintTransactions = useCallback(() => {
    window.print();
  }, []);

  // ── Transaction status badge helper ──
  function txnStatusBadge(status: string) {
    const s = (status || "").toLowerCase();
    if (s.includes("approved") || s.includes("completed") || s.includes("verified") || s.includes("paid"))
      return <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 text-[9px] font-semibold">Completed</span>;
    if (s.includes("pending") || s.includes("awaiting"))
      return <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[9px] font-semibold">Pending</span>;
    if (s.includes("rejected"))
      return <span className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 text-[9px] font-semibold">Rejected</span>;
    return <span className="inline-flex items-center rounded-full bg-gray-50 text-gray-600 border border-gray-200 px-1.5 py-0.5 text-[9px] font-semibold capitalize">{status?.replace(/_/g, " ") || "N/A"}</span>;
  }

  // ── Add Member Modal ──
  function AddMemberForm() {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [idNumber, setIdNumber] = useState("");
    const [email, setEmail] = useState("");
    const [gender, setGender] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const resetForm = () => {
      setFirstName(""); setLastName(""); setPhone(""); setIdNumber(""); setEmail(""); setGender(""); setError("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!firstName.trim() || !lastName.trim() || !idNumber.trim()) {
        setError("First name, last name, and ID number are required.");
        return;
      }
      setSaving(true);
      setError("");
      try {
        const payload = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          fullNames: `${firstName.trim()} ${lastName.trim()}`,
          phone: phone.trim(),
          idNumber: idNumber.trim(),
          email: email.trim() || undefined,
          gender: gender || undefined,
          role: "Member",
          joinedAt: new Date().toISOString().split("T")[0],
          sourceOfIncome: "Other",
          marriageStatus: "Single",
          membershipStatus: "Active",
        };
        const { data } = await api.post("/members", payload);
        const newMember = data?.data;
        toast.success("Member added successfully. You can now record contribution.");
        setShowAddMemberModal(false);
        resetForm();
        if (newMember?.id) {
          handleMemberSelect({
            memberId: newMember.id,
            memberCode: newMember.memberCode || newMember.teacherId || "",
            memberName: newMember.fullNames || `${firstName.trim()} ${lastName.trim()}`,
            phone: newMember.phone || phone.trim(),
            membershipStatus: "Active",
            totalShares: 0,
            loanBalance: 0,
            pendingFines: 0,
          });
        }
        refetchMembers();
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || "Failed to create member";
        setError(msg);
        toast.error(msg);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setShowAddMemberModal(false); resetForm(); }} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[440px] mx-4 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[#009A44]/10 flex items-center justify-center">
                <User className="h-4 w-4 text-[#009A44]" />
              </div>
              <h3 className="text-[15px] font-semibold text-gray-900">Add New Member</h3>
            </div>
            <button onClick={() => { setShowAddMemberModal(false); resetForm(); }} className="h-7 w-7 rounded-md hover:bg-gray-100 flex items-center justify-center transition-colors">
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[11px] text-red-600">{error}</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-gray-600 mb-1 block">First Name *</label>
                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-[12px] outline-none focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20 transition-all" placeholder="First name" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-600 mb-1 block">Last Name *</label>
                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-[12px] outline-none focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20 transition-all" placeholder="Last name" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-600 mb-1 block">ID Number *</label>
              <input type="text" value={idNumber} onChange={(e) => setIdNumber(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-[12px] outline-none focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20 transition-all" placeholder="National ID number" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-600 mb-1 block">Phone Number</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-[12px] outline-none focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20 transition-all" placeholder="078xxxxxxx" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-600 mb-1 block">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-[12px] outline-none focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20 transition-all" placeholder="email@example.com" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-600 mb-1 block">Gender</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-[12px] outline-none focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20 transition-all bg-white">
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button type="submit" disabled={saving}
                className="flex-1 h-9 bg-[#009A44] hover:bg-[#007a35] text-white text-[12px] font-semibold rounded-lg">
                {saving ? <><RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" /> Creating...</> : "Create Member"}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowAddMemberModal(false); resetForm(); }}
                className="h-9 text-[12px] font-medium rounded-lg">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 overflow-x-hidden">
      {/* ════════════ BREADCRUMB + HEADER ════════════ */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 pt-3 pb-0">
          <nav className="flex items-center gap-1 text-[11px] text-gray-400 mb-2">
            <button onClick={() => navigate("/savings")} className="hover:text-[#009A44] transition-colors">Savings &amp; Contributions</button>
            <span>/</span>
            <span className="text-gray-700 font-medium">Declare Monthly Shares</span>
          </nav>
        </div>
      </div>

      {/* ════════════ MODE TABS ════════════ */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide">
            {([
              { key: "single" as const, label: "Single Member", icon: User },
              { key: "multiple" as const, label: "Multiple Members", icon: Users },
              { key: "all" as const, label: "All Active Members", icon: Layers },
            ]).map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => { setMode(key); clearSelection(); }}
                className={cn("flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-medium border-b-2 transition-all -mb-px whitespace-nowrap shrink-0",
                  mode === key ? "border-[#009A44] text-[#009A44]" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                )}>
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-4 pb-28">
        {mode === "single" ? (
          /* ════════════ SINGLE MEMBER MODE ════════════ */
          <div className="space-y-5">

            {/* ═══ GLOBAL SEARCH ═══ */}
            <div className="relative">
              <Card className="border border-gray-200 shadow-sm">
                <div className="px-5 py-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      ref={searchInputRef}
                      data-search-input
                      type="text"
                      placeholder={ws ? `Search another member...` : "Search member by name, code, phone, or National ID..."}
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setSearchFocused(true); }}
                      onFocus={() => setSearchFocused(true)}
                      onKeyDown={handleKeyDown}
                      className="w-full h-11 rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-24 text-[13px] outline-none transition-all placeholder:text-gray-400 focus:border-[#009A44] focus:ring-2 focus:ring-[#009A44]/10 focus:bg-white"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      {membersLoading && !members.length && (
                        <RefreshCw className="h-3.5 w-3.5 text-gray-400 animate-spin mr-1" />
                      )}
                      {searchQuery && (
                        <button onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }}
                          className="h-6 w-6 rounded-md bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors">
                          <X className="h-3 w-3 text-gray-500" />
                        </button>
                      )}
                      <button
                        onClick={() => setShowAddMemberModal(true)}
                        className="h-6 px-1.5 rounded-md bg-[#009A44] flex items-center justify-center hover:bg-[#007a35] transition-colors"
                        title="Add New Member"
                      >
                        <span className="text-white text-[11px] font-bold leading-none">+</span>
                      </button>
                      <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[9px] font-medium text-gray-400">
                        <Keyboard className="h-2.5 w-2.5" />Ctrl+F
                      </kbd>
                    </div>
                  </div>
                  {/* Selected member indicator */}
                  {ws && (
                    <div className="mt-2 flex items-center justify-between rounded-lg bg-[#009A44]/[0.04] border border-[#009A44]/10 px-3 py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <User className="h-3 w-3 text-[#009A44] shrink-0" />
                        <span className="text-[11px] text-gray-600">Viewing:</span>
                        <span className="text-[11px] font-semibold text-gray-900 truncate">{ws.member.fullName}</span>
                        <span className="text-[10px] text-gray-400 font-mono shrink-0">{ws.member.memberCode}</span>
                      </div>
                      <button
                        onClick={clearSelection}
                        className="text-[10px] font-medium text-gray-400 hover:text-red-500 transition-colors whitespace-nowrap ml-2"
                      >
                        Clear member
                      </button>
                    </div>
                  )}
                </div>
              </Card>

              {/* ── Search Dropdown (compact banking-style overlay) ── */}
              {searchFocused && searchQuery.trim() && (
                <div data-search-dropdown className="absolute left-0 right-0 top-full mt-2 z-[100] bg-white border border-gray-200/80 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] overflow-hidden max-h-[60vh] sm:max-h-[380px] overflow-y-auto">
                  {/* Header */}
                  <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
                    <div className="flex items-center gap-2">
                      <Search className="h-3 w-3 text-gray-400" />
                      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Results</span>
                      {!membersLoading && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#009A44]/10 text-[9px] font-bold text-[#009A44] px-1">
                          {Math.min(displayMembers.length, 6)}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => { setSearchFocused(false); searchInputRef.current?.blur(); }}
                      className="text-[10px] font-medium text-[#009A44] hover:text-[#007a35] transition-colors flex items-center gap-0.5"
                    >
                      View all <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Loading skeleton */}
                  {membersLoading && (
                    <div className="py-1.5 space-y-px">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2.5 px-3.5 py-2.5 min-h-[72px]">
                          <Skeleton className="h-9 w-9 rounded-full shrink-0" />
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <Skeleton className="h-3.5 w-28" />
                            <Skeleton className="h-2.5 w-20" />
                          </div>
                          <Skeleton className="h-5 w-14 rounded-full shrink-0" />
                          <Skeleton className="h-6 w-8 shrink-0" />
                          <Skeleton className="h-5 w-16 shrink-0" />
                          <Skeleton className="h-5 w-16 shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Results — compact banking-style rows */}
                  {!membersLoading && displayMembers.length > 0 && (
                    <div ref={singleListRef} className="max-h-[380px] overflow-y-auto">
                      {displayMembers.slice(0, 6).map((member: any, idx: number) => {
                        const isActive = idx === activeIndex;
                        const loanBal = member.loanBalance || 0;
                        const fines = member.pendingFines || 0;
                        return (
                          <button
                            key={member.memberId}
                            ref={isActive ? activeItemRef : undefined}
                            data-member-row
                            onClick={() => handleMemberSelect(member)}
                            className={cn(
                              "w-full px-3.5 py-2.5 flex items-center gap-2.5 text-left transition-all border-b border-gray-100/80 last:border-0 cursor-pointer min-h-[72px] max-h-[76px]",
                              isActive
                                ? "bg-[#009A44]/[0.06] shadow-sm"
                                : "hover:bg-[#009A44]/[0.04] hover:shadow-sm"
                            )}
                          >
                            {/* Avatar */}
                            <div className={cn(
                              "flex h-9 w-9 items-center justify-center rounded-full shrink-0 text-[10px] font-bold text-white",
                              getAvatarColor(member.memberId)
                            )}>
                              {getInitials(member.memberName?.split(" ")[0], member.memberName?.split(" ").slice(1).join(" "))}
                            </div>

                            {/* Member Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-semibold text-gray-900 truncate leading-tight">
                                {highlightMatch(member.memberName || "", debouncedQuery)}
                              </p>
                              <div className="flex items-center gap-1 mt-0.5">
                                <span className="text-[10px] text-gray-400 font-mono">{member.memberCode || ""}</span>
                                {member.phone && (
                                  <>
                                    <span className="text-[10px] text-gray-300">•</span>
                                    <span className="text-[10px] text-gray-400 font-mono">{member.phone}</span>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Status */}
                            <div className="shrink-0 flex items-center justify-center">
                              <span className={cn(
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold whitespace-nowrap",
                                member.membershipStatus === "Active"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200/80"
                                  : "bg-gray-100 text-gray-600 border border-gray-200"
                              )}>
                                {member.membershipStatus || "Active"}
                              </span>
                            </div>

                            {/* Shares */}
                            <div className="shrink-0 w-[64px] text-center">
                              <p className="text-[12px] font-bold text-emerald-600 leading-tight tabular-nums">{member.totalShares || 0}</p>
                              <p className="text-[8px] text-gray-400 leading-tight uppercase tracking-wide">shares</p>
                            </div>

                            {/* Loan */}
                            <div className="shrink-0 w-[90px] text-right">
                              {loanBal > 0 ? (
                                <p className="text-[11px] font-semibold text-red-600 leading-tight tabular-nums">{formatCurrency(loanBal)}</p>
                              ) : (
                                <p className="text-[11px] text-gray-300 leading-tight tabular-nums">—</p>
                              )}
                              <p className="text-[8px] text-gray-400 leading-tight uppercase tracking-wide">loan</p>
                            </div>

                            {/* Fines */}
                            <div className="shrink-0 w-[80px] text-right">
                              {fines > 0 ? (
                                <p className="text-[11px] font-semibold text-amber-600 leading-tight tabular-nums">{formatCurrency(fines)}</p>
                              ) : (
                                <p className="text-[11px] text-gray-300 leading-tight tabular-nums">—</p>
                              )}
                              <p className="text-[8px] text-gray-400 leading-tight uppercase tracking-wide">fines</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* No results */}
                  {!membersLoading && searchQuery.trim() && displayMembers.length === 0 && (
                    <div className="px-4 py-6 text-center">
                      <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
                        <Search className="h-4 w-4 text-amber-400" />
                      </div>
                      <p className="text-[12px] font-medium text-gray-700">No member found</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">Try a different name, code, or phone number</p>
                      <button
                        onClick={() => { setShowAddMemberModal(true); setSearchFocused(false); }}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#009A44] px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-[#007a35] transition-colors"
                      >
                        <User className="h-3 w-3" /> Add New Member
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ═══ EMPTY STATE (before search) ═══ */}
            {!selectedMemberId && !searchQuery.trim() && (
              <Card className="border border-gray-200 shadow-sm">
                <CardContent className="p-16">
                  <div className="text-center max-w-sm mx-auto">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#009A44]/10">
                      <User className="h-8 w-8 text-[#009A44]" />
                    </div>
                    <h3 className="text-[16px] font-semibold text-gray-900 mb-1">Search a member to get started</h3>
                    <p className="text-[13px] text-gray-500 mb-6">
                      Search by name, code, phone, or National ID to manage contributions, loans, fines, and payments.
                    </p>
                    <div className="inline-flex flex-col items-start gap-2.5 rounded-xl bg-gray-50 px-6 py-5 text-left border border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Quick Start</p>
                      {[
                        "Search and select a member",
                        "View complete financial position",
                        "Record contribution, loan repayment, or fine",
                        "Submit for approval",
                      ].map((item, i) => (
                        <div key={item} className="flex items-center gap-2.5">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#009A44]/10 text-[10px] font-bold text-[#009A44]">{i + 1}</span>
                          <span className="text-[12px] text-gray-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ═══ WORKSPACE (after selecting member) ═══ */}
            {selectedMemberId && (
              <>
                {/* Loading skeleton */}
                {workspaceLoading && (
                  <div className="space-y-5">
                    <Card className="border border-gray-200 shadow-sm"><CardContent className="p-5 space-y-4">
                      <div className="flex items-center gap-4"><Skeleton className="h-16 w-16 rounded-full" /><div className="space-y-2"><Skeleton className="h-5 w-48" /><Skeleton className="h-3 w-32" /><Skeleton className="h-3 w-24" /></div></div>
                    </CardContent></Card>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                      <Skeleton className="h-48 rounded-xl" /><Skeleton className="h-48 rounded-xl" /><Skeleton className="h-48 rounded-xl" />
                    </div>
                  </div>
                )}

                {/* Error state — compact inline card */}
                {workspaceError && (
                  <Card className="border border-red-200 bg-red-50/50 shadow-sm">
                    <CardContent className="px-5 py-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 shrink-0 mt-0.5">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-semibold text-red-800">Unable to load member financial data</p>
                          <p className="text-[11px] text-red-600/80 mt-0.5">
                            {(workspaceError as any)?.response?.data?.message || (workspaceError as any)?.message || "Unknown error"} — ID: {selectedMemberId}
                          </p>
                        </div>
                        <button onClick={() => refetchWorkspace()} className="inline-flex items-center gap-1 rounded-lg bg-white border border-red-200 px-2.5 py-1.5 text-[11px] font-medium text-red-700 hover:bg-red-50 transition-colors shrink-0">
                          <RefreshCw className="h-3 w-3" /> Retry
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* ═══ WORKSPACE CONTENT ═══ */}
                {hasProfile && ws && (
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] xl:grid-cols-[1fr_420px] gap-5">

                    {/* ═══ LEFT: FINANCIAL DATA ═══ */}
                    <div className="space-y-5 min-w-0">

                      {/* ── A. Member Header Card ── */}
                      <Card className="border border-gray-200/60 shadow-md overflow-hidden rounded-2xl">
                        <div className="bg-gradient-to-br from-[#009A44] via-[#008A3C] to-[#006B2E] px-6 py-5 sm:px-8 sm:py-6">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                            {/* Left: Avatar + Info */}
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <div className="flex h-[56px] w-[56px] items-center justify-center rounded-full bg-white/[0.18] text-white text-[20px] font-bold border-2 border-white/[0.25] shrink-0 backdrop-blur-sm">
                                {getInitials(ws.member.firstName, ws.member.lastName)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2.5 flex-wrap">
                                  <p className="text-[20px] font-bold text-white truncate leading-tight">{ws.member.fullName}</p>
                                  <span className="inline-flex items-center rounded-full bg-white/[0.18] backdrop-blur-sm px-2.5 py-0.5 text-[10px] font-semibold text-white border border-white/[0.2] shrink-0">
                                    {ws.member.status === "Active" ? "Active Member" : ws.member.status}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 mt-1.5 text-[12px] text-white/70">
                                  <span className="flex items-center gap-1"><Hash className="h-3 w-3 text-white/40" /><span className="text-white/50">Member Code:</span> <span className="font-mono text-white/80">{ws.member.memberCode}</span></span>
                                  {ws.member.phone && (
                                    <>
                                      <span className="text-white/20">•</span>
                                      <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-white/40" /><span className="text-white/50">Phone:</span> <span className="font-mono text-white/80">{ws.member.phone}</span></span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Right: Join Date + Member Since */}
                            <div className="flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap sm:flex-nowrap">
                              <div className="flex items-center gap-2.5 bg-white/[0.12] backdrop-blur-sm rounded-xl px-3.5 py-2.5 border border-white/[0.12]">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.15]">
                                  <Calendar className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                  <p className="text-[9px] text-white/50 uppercase tracking-wider font-medium">Join Date</p>
                                  <p className="text-[13px] font-semibold text-white leading-tight">
                                    {ws.member.joinedAt ? new Date(ws.member.joinedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "N/A"}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2.5 bg-white/[0.12] backdrop-blur-sm rounded-xl px-3.5 py-2.5 border border-white/[0.12]">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.15]">
                                  <Clock className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                  <p className="text-[9px] text-white/50 uppercase tracking-wider font-medium">Member Since</p>
                                  <p className="text-[13px] font-semibold text-white leading-tight">
                                    {ws.member.memberSince >= 12
                                      ? `${Math.floor(ws.member.memberSince / 12)} Year${Math.floor(ws.member.memberSince / 12) !== 1 ? "s" : ""}`
                                      : `${ws.member.memberSince} Month${ws.member.memberSince !== 1 ? "s" : ""}`}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>

                      {/* ── B. Summary Cards (3 columns) ── */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                        {/* ── Card 1: Contribution Summary ── */}
                        <Card className="border border-gray-200 shadow-sm">
                          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50"><PiggyBank className="h-3.5 w-3.5 text-emerald-600" /></div>
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Contribution Summary</p>
                          </div>
                          <div className="p-5 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Total Contributions</span>
                              <span className="text-[14px] font-bold text-emerald-600">{formatCurrency(ws.contributionSummary.totalContributions)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Contribution Period</span>
                              <span className="text-[11px] font-medium text-gray-700">
                                {ws.contributionSummary.contributionPeriod
                                  ? `${new Date(ws.contributionSummary.contributionPeriod.from).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })} — ${new Date(ws.contributionSummary.contributionPeriod.to).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}`
                                  : "No previous contribution"}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Previous Contribution</span>
                              <span className="text-[12px] font-semibold text-gray-700">{ws.contributionSummary.previousContribution > 0 ? formatCurrency(ws.contributionSummary.previousContribution) : "No previous contribution"}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Fine Accumulations</span>
                              <span className={cn("text-[12px] font-semibold", ws.contributionSummary.contributionFineAccumulation > 0 ? "text-red-600" : "text-emerald-600")}>{ws.contributionSummary.contributionFineAccumulation > 0 ? formatCurrency(ws.contributionSummary.contributionFineAccumulation) : "No fines"}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Current Contribution Date</span>
                              <span className="text-[11px] font-medium text-gray-700">
                                {ws.contributionSummary.lastContributionDate
                                  ? new Date(ws.contributionSummary.lastContributionDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                                  : "No contributions yet"}
                              </span>
                            </div>
                            <div className="h-px bg-gray-100" />
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Current Month Status</span>
                              <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold",
                                ws.contributionSummary.currentMonthStatus === "approved" || ws.contributionSummary.currentMonthStatus === "completed"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200/80"
                                  : ws.contributionSummary.currentMonthStatus === "not_declared"
                                    ? "bg-gray-100 text-gray-500 border border-gray-200"
                                    : "bg-amber-50 text-amber-700 border border-amber-200/80"
                              )}>
                                {ws.contributionSummary.currentMonthStatus === "not_declared" ? "Not Declared" : ws.contributionSummary.currentMonthStatus.replace(/_/g, " ")}
                              </span>
                            </div>
                          </div>
                        </Card>

                        {/* ── Card 2: Loan Summary ── */}
                        <Card className="border border-gray-200 shadow-sm">
                          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50"><Wallet className="h-3.5 w-3.5 text-blue-600" /></div>
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Loan Summary</p>
                          </div>
                          <div className="p-5 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Active Loan</span>
                              <span className={cn("text-[12px] font-semibold", ws.loansSummary.hasActiveLoan ? "text-red-600" : "text-emerald-600")}>{ws.loansSummary.hasActiveLoan ? formatCurrency(ws.loanSummary?.outstandingBalance || 0) : "No active loan"}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Paid Loans</span>
                              <span className="text-[12px] font-semibold text-gray-700">{ws.loansSummary.paidLoanCount} loan{ws.loansSummary.paidLoanCount !== 1 ? "s" : ""}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Outstanding Loan</span>
                              <span className={cn("text-[12px] font-bold", ws.loansSummary.totalLoanOutstanding > 0 ? "text-red-600" : "text-emerald-600")}>{ws.loansSummary.totalLoanOutstanding > 0 ? formatCurrency(ws.loansSummary.totalLoanOutstanding) : "No outstanding"}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Monthly Installment</span>
                              <span className="text-[12px] font-semibold text-gray-700">{ws.loanSummary?.monthlyInstallment ? formatCurrency(ws.loanSummary.monthlyInstallment) : "—"}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Loan Fine Accumulation</span>
                              <span className={cn("text-[12px] font-semibold", ws.loansSummary.loanFineAccumulation > 0 ? "text-red-600" : "text-emerald-600")}>{ws.loansSummary.loanFineAccumulation > 0 ? formatCurrency(ws.loansSummary.loanFineAccumulation) : "No fines"}</span>
                            </div>
                            <div className="h-px bg-gray-100" />
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Loan Status</span>
                              <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold",
                                ws.loanSummary?.status === "active" ? "bg-amber-50 text-amber-700 border border-amber-200/80" :
                                ws.loanSummary?.status === "overdue" ? "bg-red-50 text-red-700 border border-red-200/80" :
                                "bg-gray-100 text-gray-500 border border-gray-200"
                              )}>
                                {ws.loanSummary?.status ? ws.loanSummary.status.replace(/_/g, " ") : "No loan"}
                              </span>
                            </div>
                          </div>
                        </Card>

                        {/* ── Card 3: Account Health ── */}
                        <Card className="border border-gray-200 shadow-sm">
                          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-50"><Activity className="h-3.5 w-3.5 text-violet-600" /></div>
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Account Health</p>
                          </div>
                          <div className="p-5 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Contribution Regularity</span>
                              <span className={cn("text-[12px] font-semibold",
                                ws.accountHealth.contributionRegularity === "Excellent" ? "text-emerald-600" :
                                ws.accountHealth.contributionRegularity === "Good" ? "text-blue-600" :
                                ws.accountHealth.contributionRegularity === "Fair" ? "text-amber-600" :
                                "text-gray-500"
                              )}>
                                {ws.accountHealth.contributionRegularity === "N/A" ? "New member" : ws.accountHealth.contributionRegularity}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Loan Risk Level</span>
                              <span className={cn("text-[12px] font-semibold",
                                ws.accountHealth.loanRiskLevel === "None" ? "text-gray-500" :
                                ws.accountHealth.loanRiskLevel === "Low" ? "text-emerald-600" :
                                ws.accountHealth.loanRiskLevel === "Medium" ? "text-amber-600" :
                                "text-red-600"
                              )}>
                                {ws.accountHealth.loanRiskLevel === "None" ? "No loan" : ws.accountHealth.loanRiskLevel}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Outstanding Fines</span>
                              <span className={cn("text-[12px] font-semibold", ws.accountHealth.outstandingFines > 0 ? "text-red-600" : "text-emerald-600")}>{ws.accountHealth.outstandingFines > 0 ? formatCurrency(ws.accountHealth.outstandingFines) : "No fines"}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-gray-500">Days Since Last Contribution</span>
                              <span className="text-[11px] font-medium text-gray-700">
                                {ws.accountHealth.daysSinceLastContribution !== null
                                  ? `${ws.accountHealth.daysSinceLastContribution} day${ws.accountHealth.daysSinceLastContribution !== 1 ? "s" : ""} ago`
                                  : "New member"}
                              </span>
                            </div>
                            <div className="h-px bg-gray-100" />
                            <button className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-[#009A44]/5 border border-[#009A44]/10 px-3 py-2 text-[11px] font-medium text-[#009A44] hover:bg-[#009A44]/10 transition-colors">
                              <Stethoscope className="h-3 w-3" />
                              View Full Analysis
                            </button>
                          </div>
                        </Card>
                      </div>

                      {/* ── D. Missing Contributions & Fines ── */}
                      {ws.missingContributions?.months && ws.missingContributions.months.length > 0 ? (
                        <Card className="border border-gray-200 shadow-sm overflow-hidden rounded-2xl">
                          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-50"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /></div>
                            <h3 className="text-[13px] font-semibold text-gray-900">Missing Contributions &amp; Fines</h3>
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-100 text-[9px] font-bold text-amber-700 px-1 ml-1">{ws.missingContributions?.summary.missingMonthsCount}</span>
                          </div>

                          {/* Summary cards */}
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-5 py-4 bg-gray-50/50">
                            <div className="rounded-xl bg-white border border-gray-200/60 px-3.5 py-2.5">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Missing Months</p>
                              <p className="text-[18px] font-bold text-amber-600 mt-0.5">{ws.missingContributions?.summary.missingMonthsCount}</p>
                            </div>
                            <div className="rounded-xl bg-white border border-gray-200/60 px-3.5 py-2.5">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Contributions Total</p>
                              <p className="text-[14px] font-bold text-gray-900 mt-0.5">{formatCurrency(ws.missingContributions?.summary.missingContributionsTotal || 0)}</p>
                            </div>
                            <div className="rounded-xl bg-white border border-gray-200/60 px-3.5 py-2.5">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Fine Accumulation</p>
                              <p className={cn("text-[14px] font-bold mt-0.5", (ws.missingContributions?.summary.missingFinesTotal || 0) > 0 ? "text-red-600" : "text-emerald-600")}>{formatCurrency(ws.missingContributions?.summary.missingFinesTotal || 0)}</p>
                            </div>
                            <div className="rounded-xl bg-white border border-gray-200/60 px-3.5 py-2.5">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Total Due</p>
                              <p className="text-[14px] font-bold text-red-600 mt-0.5">{formatCurrency(ws.missingContributions?.summary.missingDueTotal || 0)}</p>
                            </div>
                          </div>

                          {/* Table */}
                          <div className="overflow-x-auto -mx-5 px-5">
                            <table className="w-full min-w-[540px]">
                              <thead>
                                <tr className="bg-[#009A44]/5 border-b border-gray-100">
                                  <th className="text-left px-3 sm:px-5 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Month</th>
                                  <th className="text-right px-3 sm:px-5 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Required</th>
                                  <th className="text-right px-3 sm:px-5 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Fine</th>
                                  <th className="text-right px-3 sm:px-5 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Total Due</th>
                                  <th className="text-center px-3 sm:px-5 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Status</th>
                                  <th className="text-center px-3 sm:px-5 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Action</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50">
                                {(ws.missingContributions?.months || []).map((m) => (
                                  <tr key={`${m.year}-${m.month}`} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-3 sm:px-5 py-2.5 sm:py-3 text-[11px] sm:text-[12px] font-medium text-gray-900">{m.monthName} {m.year}</td>
                                    <td className="px-3 sm:px-5 py-2.5 sm:py-3 text-[11px] sm:text-[12px] text-gray-700 text-right">{formatCurrency(m.requiredContribution)}</td>
                                    <td className="px-3 sm:px-5 py-2.5 sm:py-3 text-[11px] sm:text-[12px] text-right font-semibold">
                                      {m.fineAmount > 0 ? (
                                        <span className="text-red-600">{formatCurrency(m.fineAmount)}</span>
                                      ) : (
                                        <span className="text-emerald-600">0 FRW</span>
                                      )}
                                    </td>
                                    <td className="px-3 sm:px-5 py-2.5 sm:py-3 text-[11px] sm:text-[12px] font-bold text-gray-900 text-right">{formatCurrency(m.totalDue)}</td>
                                    <td className="px-3 sm:px-5 py-2.5 sm:py-3 text-center">
                                      <span className={cn("inline-flex items-center rounded-full px-1.5 sm:px-2 py-0.5 text-[8px] sm:text-[9px] font-semibold",
                                        m.status === "unpaid" ? "bg-red-50 text-red-700 border border-red-200/80" :
                                        m.status === "pending_approval" ? "bg-amber-50 text-amber-700 border border-amber-200/80" :
                                        "bg-blue-50 text-blue-700 border border-blue-200/80"
                                      )}>
                                        {m.status === "unpaid" ? "Unpaid" : m.status === "pending_approval" ? "Pending" : "Declared"}
                                      </span>
                                    </td>
                                    <td className="px-3 sm:px-5 py-2.5 sm:py-3 text-center">
                                      <button
                                        onClick={() => settleMissingMonth(m.month, m.year, m.requiredContribution, m.fineAmount)}
                                        className="inline-flex items-center gap-1 rounded-lg bg-[#009A44]/10 border border-[#009A44]/20 px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[10px] font-semibold text-[#009A44] hover:bg-[#009A44]/20 transition-colors"
                                      >
                                        <DollarSign className="h-3 w-3" /> Settle
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Card>
                      ) : (
                        <Card className="border border-gray-200 shadow-sm rounded-2xl">
                          <div className="px-5 py-6 flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50"><CheckCircle2 className="h-4.5 w-4.5 text-emerald-500" /></div>
                            <div>
                              <p className="text-[12px] font-semibold text-gray-900">No missing contributions</p>
                              <p className="text-[11px] text-gray-500">Member is up to date with all contributions.</p>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* ── F. Monthly Savings History ── */}
                      <Card className="border border-gray-200 shadow-sm overflow-hidden rounded-2xl">
                        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50"><BarChart3 className="h-3.5 w-3.5 text-blue-600" /></div>
                            <h3 className="text-[13px] font-semibold text-gray-900">Monthly Savings History</h3>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => window.print()} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                              <Printer className="h-3 w-3" /> Print
                            </button>
                            <button onClick={() => toast.info("Export coming soon")} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                              <Download className="h-3 w-3" /> Export
                            </button>
                          </div>
                        </div>

                        {savingsHistoryLoading ? (
                          <div className="p-5 space-y-3">
                            <div className="grid grid-cols-5 gap-3">
                              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
                            </div>
                            <Skeleton className="h-48 rounded-xl" />
                          </div>
                        ) : savingsHistory ? (
                          <>
                            {/* Summary cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 px-5 py-4 bg-gray-50/50">
                              <div className="rounded-xl bg-white border border-gray-200/60 px-3 py-2.5">
                                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-medium">Expected</p>
                                <p className="text-[16px] font-bold text-gray-900 mt-0.5">{savingsHistory.summary.monthsExpected}</p>
                              </div>
                              <div className="rounded-xl bg-white border border-gray-200/60 px-3 py-2.5">
                                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-medium">Saved</p>
                                <p className="text-[16px] font-bold text-emerald-600 mt-0.5">{savingsHistory.summary.monthsSaved}</p>
                              </div>
                              <div className="rounded-xl bg-white border border-gray-200/60 px-3 py-2.5">
                                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-medium">Missed</p>
                                <p className={cn("text-[16px] font-bold mt-0.5", savingsHistory.summary.missedMonths > 0 ? "text-red-600" : "text-gray-900")}>{savingsHistory.summary.missedMonths}</p>
                              </div>
                              <div className="rounded-xl bg-white border border-gray-200/60 px-3 py-2.5">
                                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-medium">Total Fines</p>
                                <p className={cn("text-[14px] font-bold mt-0.5", savingsHistory.summary.totalFines > 0 ? "text-red-600" : "text-emerald-600")}>{formatCurrency(savingsHistory.summary.totalFines)}</p>
                              </div>
                              <div className="rounded-xl bg-white border border-gray-200/60 px-3 py-2.5">
                                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-medium">Consistency</p>
                                <p className={cn("text-[16px] font-bold mt-0.5",
                                  savingsHistory.summary.savingsConsistencyPercent >= 80 ? "text-emerald-600" :
                                  savingsHistory.summary.savingsConsistencyPercent >= 50 ? "text-amber-600" : "text-red-600"
                                )}>{savingsHistory.summary.savingsConsistencyPercent}%</p>
                              </div>
                            </div>

                            {/* Filters */}
                            <div className="px-5 py-3 flex items-center gap-3 flex-wrap border-b border-gray-100">
                              <div className="flex items-center gap-1.5">
                                <Calendar className="h-3 w-3 text-gray-400" />
                                <select value={historyYear} onChange={(e) => setHistoryYear(Number(e.target.value))}
                                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium focus:border-[#009A44] outline-none">
                                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                  ))}
                                </select>
                              </div>
                              <select value={historyStatusFilter} onChange={(e) => setHistoryStatusFilter(e.target.value)}
                                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium focus:border-[#009A44] outline-none">
                                <option value="all">All Status</option>
                                <option value="paid">Paid</option>
                                <option value="pending_approval">Pending</option>
                                <option value="missed">Missed</option>
                                <option value="late">Late</option>
                                <option value="partial">Partial</option>
                                <option value="fine_applied">Fine Applied</option>
                              </select>
                              <select value={historyMethodFilter} onChange={(e) => setHistoryMethodFilter(e.target.value)}
                                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium focus:border-[#009A44] outline-none">
                                <option value="all">All Methods</option>
                                <option value="Salary Deduction">Salary Deduction</option>
                                <option value="Cash">Cash</option>
                                <option value="Mobile Money">Mobile Money</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                              </select>
                              <span className="text-[10px] text-gray-400 ml-auto">{filteredHistoryMonths.length} month{filteredHistoryMonths.length !== 1 ? "s" : ""}</span>
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto -mx-5 px-5">
                              <table className="w-full min-w-[700px]">
                                <thead>
                                  <tr className="bg-[#009A44]/5 border-b border-gray-100">
                                    <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Month</th>
                                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Required</th>
                                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Paid</th>
                                    <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Shares</th>
                                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Method</th>
                                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Date</th>
                                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Fine</th>
                                    <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Status</th>
                                    <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Approval</th>
                                    <th className="text-center px-5 py-2.5 text-[10px] font-semibold text-[#009A44] uppercase tracking-wider">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                  {filteredHistoryMonths.map((m) => (
                                    <tr key={`${m.year}-${m.month}`} className="hover:bg-gray-50/50 transition-colors">
                                      <td className="px-5 py-3 text-[12px] font-medium text-gray-900">{m.monthName}</td>
                                      <td className="px-4 py-3 text-[11px] text-gray-600 text-right tabular-nums">{formatCurrency(m.requiredContribution)}</td>
                                      <td className="px-4 py-3 text-[11px] text-right tabular-nums font-semibold">
                                        {m.declaredContribution > 0 ? (
                                          <span className="text-emerald-600">{formatCurrency(m.declaredContribution)}</span>
                                        ) : (
                                          <span className="text-gray-300">—</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-[11px] text-center">
                                        {m.sharesPurchased > 0 ? (
                                          <span className="text-[#009A44] font-semibold">{m.sharesPurchased}</span>
                                        ) : (
                                          <span className="text-gray-300">—</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-[10px] text-gray-500">{m.paymentMethod || "—"}</td>
                                      <td className="px-4 py-3 text-[10px] text-gray-500">
                                        {m.paymentDate ? new Date(m.paymentDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                                      </td>
                                      <td className="px-4 py-3 text-[11px] text-right font-semibold">
                                        {m.fineAmount > 0 ? (
                                          <span className="text-red-600 tabular-nums">{formatCurrency(m.fineAmount)}</span>
                                        ) : (
                                          <span className="text-emerald-600">0</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold",
                                          m.status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200/80" :
                                          m.status === "pending_approval" ? "bg-amber-50 text-amber-700 border border-amber-200/80" :
                                          m.status === "missed" ? "bg-red-50 text-red-700 border border-red-200/80" :
                                          m.status === "late" ? "bg-orange-50 text-orange-700 border border-orange-200/80" :
                                          m.status === "partial" ? "bg-blue-50 text-blue-700 border border-blue-200/80" :
                                          m.status === "fine_applied" ? "bg-red-50 text-red-700 border border-red-200/80" :
                                          "bg-gray-100 text-gray-600 border border-gray-200"
                                        )}>
                                          {m.status === "paid" ? "Paid" :
                                           m.status === "pending_approval" ? "Pending" :
                                           m.status === "missed" ? "Missed" :
                                           m.status === "late" ? "Late" :
                                           m.status === "partial" ? "Partial" :
                                           m.status === "fine_applied" ? "Fine" : m.status}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-center text-[10px] text-gray-500">
                                        {m.approvalProgress !== "0/3" ? (
                                          <span className="font-medium">{m.approvalProgress}</span>
                                        ) : (
                                          <span className="text-gray-300">—</span>
                                        )}
                                      </td>
                                      <td className="px-5 py-3 text-center">
                                        {(m.status === "missed" || m.status === "fine_applied" || m.status === "late") && (
                                          <button
                                            onClick={() => settleMissingMonth(m.month, m.year, m.requiredContribution, m.fineAmount)}
                                            className="inline-flex items-center gap-1 rounded-lg bg-[#009A44]/10 border border-[#009A44]/20 px-2.5 py-1 text-[9px] font-semibold text-[#009A44] hover:bg-[#009A44]/20 transition-colors"
                                          >
                                            <DollarSign className="h-2.5 w-2.5" /> Settle
                                          </button>
                                        )}
                                        {m.status === "paid" && (
                                          <span className="text-[9px] text-emerald-600 font-medium flex items-center gap-1 justify-center">
                                            <CheckCircle2 className="h-3 w-3" /> Done
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        ) : (
                          <div className="px-5 py-8 text-center">
                            <p className="text-[12px] text-gray-500">No savings history available</p>
                          </div>
                        )}
                      </Card>

                      {/* ── G. Recent Transactions (DataTable) ── */}
                      <DataTable
                        columns={txnColumns}
                        data={paginatedTxns}
                        facets={txnFacets}
                        isLoading={workspaceLoading}
                        isFetching={workspaceLoading}
                        onSearch={setTxnSearch}
                        sorting={txnSorting}
                        setSorting={setTxnSorting}
                        columnFilters={txnColumnFilters}
                        setColumnFilters={setTxnColumnFilters}
                        setPagination={setTxnPagination}
                        pageIndex={txnPagination.pageIndex}
                        pageSize={txnPagination.pageSize}
                        pageCount={txnPageCount}
                        enableClientFiltering
                        onExport={handleExportTransactions}
                        emptyMessage="No transactions yet"
                        emptyDescription="Transactions will appear here once recorded."
                        loadingRows={5}
                      />
                    </div>

                    {/* ═══ RIGHT: PAYMENT SETTLEMENT PANEL ═══ */}
                    <div className="lg:sticky lg:top-24 lg:self-start space-y-4">

                      {/* ── Settlement Tabs ── */}
                      <Card className="border border-gray-200 shadow-sm overflow-hidden">
                        <div className="flex border-b border-gray-100">
                          {([
                            { key: "contribution" as const, label: "Contribution", icon: PiggyBank },
                            { key: "loan" as const, label: "Loan Repayment", icon: Wallet },
                            { key: "fine" as const, label: "Fine Payment", icon: AlertTriangle },
                            { key: "mixed" as const, label: "Mixed", icon: Repeat },
                          ]).map(({ key, label, icon: Icon }) => (
                            <button key={key} onClick={() => setSettlementTab(key)}
                              className={cn("flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] font-medium transition-all border-b-2 -mb-px",
                                settlementTab === key ? "border-[#009A44] text-[#009A44] bg-[#009A44]/[0.03]" : "border-transparent text-gray-500 hover:text-gray-700"
                              )}>
                              <Icon className="h-3 w-3" />
                              <span className="hidden sm:inline">{label}</span>
                            </button>
                          ))}
                        </div>

                        <div className="p-4 space-y-4">
                          {/* ── Contribution Amount ── */}
                          {(settlementTab === "contribution" || settlementTab === "mixed") && (
                            <div>
                              <Label className="text-[12px] font-medium text-gray-700">Contribution Amount (FRW) *</Label>
                              <Input type="number" value={contributionAmount}
                                onChange={(e) => setContributionAmount(e.target.value)}
                                min={minimumAmount}
                                placeholder={`Min: ${formatCurrency(minimumAmount)}`}
                                className={cn("h-10 mt-1.5 text-[13px] rounded-lg",
                                  isBelowMinimum && "border-red-300 focus:border-red-500 focus:ring-red-500"
                                )} />
                              {isBelowMinimum && (
                                <p className="mt-1 text-[11px] text-red-500 flex items-center gap-1">
                                  <AlertOctagon className="h-3 w-3" /> Min {formatCurrency(minimumAmount)}
                                </p>
                              )}
                              {contributionNum > 0 && (
                                <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-emerald-600">
                                  <TrendingUp className="h-3 w-3" />
                                  <span>{sharesFromContribution} share{sharesFromContribution !== "1" ? "s" : ""} ({formatCurrency(SHARE_VALUE)} each)</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* ── Loan Repayment ── */}
                          {(settlementTab === "loan" || settlementTab === "mixed") && ws.loanSummary && (
                            <div>
                              <Label className="text-[12px] font-medium text-gray-700">Loan Repayment (FRW)</Label>
                              <Input type="number" value={loanRepaymentAmount}
                                onChange={(e) => setLoanRepaymentAmount(e.target.value)}
                                min={0} max={ws.loanSummary.outstandingBalance}
                                placeholder={`Max: ${formatCurrency(ws.loanSummary.outstandingBalance)}`}
                                className="h-10 mt-1.5 text-[13px] rounded-lg" />
                              {loanRepaymentNum > 0 && loanRepaymentNum > ws.loanSummary.outstandingBalance && (
                                <p className="mt-1 text-[11px] text-red-500 flex items-center gap-1">
                                  <AlertOctagon className="h-3 w-3" /> Exceeds balance
                                </p>
                              )}
                              <p className="mt-1 text-[10px] text-gray-400">Balance: {formatCurrency(ws.loanSummary.outstandingBalance)}</p>
                            </div>
                          )}
                          {(settlementTab === "loan" || settlementTab === "mixed") && !ws.loanSummary && (
                            <div className="rounded-lg bg-gray-50 p-3 text-center">
                              <p className="text-[11px] text-gray-500">No active loan</p>
                            </div>
                          )}

                          {/* ── Fine Payment ── */}
                          {(settlementTab === "fine" || settlementTab === "mixed") && ws.fines.pendingFines > 0 && (
                            <div>
                              <Label className="text-[12px] font-medium text-gray-700">Fine Amount (FRW)</Label>
                              <Input type="number" value={fineAmount}
                                onChange={(e) => setFineAmount(e.target.value)}
                                min={0} max={ws.fines.pendingFines}
                                placeholder={`Max: ${formatCurrency(ws.fines.pendingFines)}`}
                                className="h-10 mt-1.5 text-[13px] rounded-lg" />
                              <p className="mt-1 text-[10px] text-gray-400">Pending: {formatCurrency(ws.fines.pendingFines)}</p>
                            </div>
                          )}
                          {(settlementTab === "fine" || settlementTab === "mixed") && ws.fines.pendingFines === 0 && (
                            <div className="rounded-lg bg-emerald-50 p-3 text-center">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto mb-1" />
                              <p className="text-[11px] text-emerald-600 font-medium">No pending fines</p>
                            </div>
                          )}

                          <div className="h-px bg-gray-100" />

                          {/* ── Payment Details ── */}
                          <div>
                            <Label className="text-[12px] font-medium text-gray-700">Payment Method</Label>
                            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
                              className="mt-1.5 w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-[12px] font-medium focus:border-[#009A44] outline-none">
                              {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-[12px] font-medium text-gray-700">Payment Date</Label>
                              <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                                className="h-10 mt-1.5 text-[12px] rounded-lg" />
                            </div>
                            <div>
                              <Label className="text-[12px] font-medium text-gray-700">Reference # {needsReference ? <span className="text-red-500">*</span> : ""}</Label>
                              <Input type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                                placeholder={needsReference ? "Required" : "Optional"} className={cn("h-10 mt-1.5 text-[12px] rounded-lg", missingReference && "border-red-300 focus:border-red-400")} />
                            </div>
                          </div>
                          <div>
                            <Label className="text-[12px] font-medium text-gray-700">Notes</Label>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                              placeholder="Optional notes..." rows={2}
                              className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] focus:border-[#009A44] focus:ring-2 focus:ring-[#009A44]/10 outline-none resize-none" />
                          </div>
                        </div>
                      </Card>

                      {/* ── Payment Summary ── */}
                      {(totalPayment > 0) && (
                        <Card className="border border-gray-200 shadow-sm">
                          <div className="px-4 py-3 border-b border-gray-100">
                            <h4 className="text-[12px] font-semibold text-gray-900">Payment Summary</h4>
                          </div>
                          <div className="p-4 space-y-2">
                            {contributionNum > 0 && (
                              <div className="flex items-center justify-between py-1">
                                <span className="text-[11px] text-gray-500">Contribution</span>
                                <span className="text-[12px] font-semibold text-emerald-600">{formatCurrency(contributionNum)}</span>
                              </div>
                            )}
                            {loanRepaymentNum > 0 && (
                              <div className="flex items-center justify-between py-1">
                                <span className="text-[11px] text-gray-500">Loan Repayment</span>
                                <span className="text-[12px] font-semibold text-blue-600">{formatCurrency(loanRepaymentNum)}</span>
                              </div>
                            )}
                            {fineNum > 0 && (
                              <div className="flex items-center justify-between py-1">
                                <span className="text-[11px] text-gray-500">Fine Payment</span>
                                <span className="text-[12px] font-semibold text-amber-600">{formatCurrency(fineNum)}</span>
                              </div>
                            )}
                            {contributionNum > 0 && (
                              <div className="flex items-center justify-between py-1">
                                <span className="text-[11px] text-gray-500">Shares</span>
                                <span className="text-[12px] font-semibold text-[#009A44]">{sharesFromContribution} shares</span>
                              </div>
                            )}
                            <div className="h-px bg-gray-100" />
                            <div className="flex items-center justify-between py-1.5">
                              <span className="text-[13px] font-bold text-gray-900">Total Payment</span>
                              <span className="text-[16px] font-bold text-[#009A44]">{formatCurrency(totalPayment)}</span>
                            </div>
                            {ws.loanSummary && (
                              <div className="flex items-center justify-between py-1">
                                <span className="text-[10px] text-gray-500">Remaining Loan</span>
                                <span className={cn("text-[11px] font-semibold", (ws.loanSummary.outstandingBalance - loanRepaymentNum) > 0 ? "text-red-600" : "text-emerald-600")}>
                                  {formatCurrency(Math.max(0, ws.loanSummary.outstandingBalance - loanRepaymentNum))}
                                </span>
                              </div>
                            )}
                            {ws.fines.pendingFines > 0 && (
                              <div className="flex items-center justify-between py-1">
                                <span className="text-[10px] text-gray-500">Remaining Fines</span>
                                <span className={cn("text-[11px] font-semibold", (ws.fines.pendingFines - fineNum) > 0 ? "text-red-600" : "text-emerald-600")}>
                                  {formatCurrency(Math.max(0, ws.fines.pendingFines - fineNum))}
                                </span>
                              </div>
                            )}
                          </div>
                        </Card>
                      )}

                      {/* ── Add to Existing Badge ── */}
                      {ws.existingDeclaration && declarationMode === "add_to_existing" && (
                        <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 flex items-center gap-2">
                          <Layers className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                          <div>
                            <p className="text-[11px] text-blue-700 font-medium">Adding to existing pending declaration</p>
                            <p className="text-[10px] text-blue-500">
                              Current: {formatCurrency(ws.existingDeclaration.declaredAmount)} — {ws.existingDeclaration.approvalCount}/{ws.existingDeclaration.requiredApprovals} approvals
                            </p>
                          </div>
                        </div>
                      )}

                      {/* ── Existing Declaration Warning (new mode) ── */}
                      {ws.existingDeclaration && declarationMode === "new" && (
                        <Card className="border border-amber-200 shadow-sm bg-amber-50">
                          <div className="p-3 flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                            <p className="text-[11px] text-amber-700">
                              Existing declaration: {formatCurrency(ws.existingDeclaration.declaredAmount)} — {ws.existingDeclaration.approvalCount}/{ws.existingDeclaration.requiredApprovals} approvals
                            </p>
                          </div>
                        </Card>
                      )}

                      {/* ── Approval Info ── */}
                      <div className="flex items-center justify-between rounded-lg bg-[#009A44]/5 px-4 py-2.5 border border-[#009A44]/10">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#009A44]" />
                          <span className="text-[11px] text-gray-600">Requires 3 leader approvals</span>
                        </div>
                        <span className="inline-flex rounded-full bg-[#009A44]/10 px-2 py-0.5 text-[10px] font-semibold text-[#009A44]">Awaiting Approval</span>
                      </div>

                      {/* ── Validation Hints ── */}
                      {!canSubmit && totalPayment > 0 && (
                        <div className="rounded-lg bg-amber-50 border border-amber-200/60 px-3 py-2 space-y-0.5">
                          {isBelowMinimum && (
                            <p className="text-[10px] text-amber-600">Amount below minimum of {formatCurrency(minimumAmount)}</p>
                          )}
                          {missingReference && (
                            <p className="text-[10px] text-amber-600">Reference # is required for {paymentMethod}</p>
                          )}
                        </div>
                      )}

                      {/* ── Period Selector ── */}
                      <Card className="border border-gray-200 shadow-sm">
                        <div className="p-3 flex items-center gap-3">
                          <Calendar className="h-3.5 w-3.5 text-gray-400" />
                          <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium focus:border-[#009A44] outline-none flex-1">
                            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                          </select>
                          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="w-20 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium focus:border-[#009A44] outline-none">
                            {years.map((y) => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                      </Card>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
        /* ════════════ BULK DECLARATION VIEW ════════════ */
        <div className="space-y-4">
          {/* ── Filters Row ── */}
          <Card className="border border-gray-200 shadow-sm">
            <div className="px-4 py-3 flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                <input type="text" placeholder="Search members..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-9 rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-3 text-[12px] outline-none focus:border-[#009A44] focus:ring-2 focus:ring-[#009A44]/10 focus:bg-white transition-all" />
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3 w-3 text-gray-400" />
                <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] focus:border-[#009A44] outline-none">
                  {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
                <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="w-18 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] focus:border-[#009A44] outline-none">
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <Wallet className="h-3 w-3 text-gray-400" />
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] focus:border-[#009A44] outline-none">
                  {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </Card>

          {/* ── Status Filter Tabs ── */}
          <StatusFilterTabs activeFilter={bulkStatusFilter} onFilterChange={setBulkStatusFilter} counts={counts} />

          {/* ── Member List ── */}
          <Card className="border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-3">
              <input type="checkbox"
                checked={members.length > 0 && members.filter((m: any) => m.canDeclare).every((m: any) => bulkSelectedIds.has(m.memberId))}
                onChange={toggleAllVisible}
                className="h-3.5 w-3.5 rounded border-gray-300 text-[#009A44] focus:ring-[#009A44]" />
              <span className="text-[11px] font-medium text-gray-500 flex-1">
                {bulkSelectedIds.size > 0 ? `${bulkSelectedIds.size} selected` : `${totalMembers} members`}
              </span>
              <button onClick={() => queryClient.invalidateQueries("declaration-members")}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>

            {membersLoading && (
              <div className="divide-y divide-gray-50">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="px-4 py-3 flex items-center gap-3">
                    <Skeleton className="h-3.5 w-3.5 rounded shrink-0" />
                    <Skeleton className="h-9 w-9 rounded-full shrink-0" />
                    <div className="flex-1 space-y-1.5"><Skeleton className="h-3 w-32" /><Skeleton className="h-2.5 w-20" /></div>
                    <Skeleton className="h-5 w-16 rounded-full shrink-0" />
                  </div>
                ))}
              </div>
            )}

            {!membersLoading && members.length > 0 && (
              <div className="divide-y divide-gray-50">
                {members.map((member: any) => {
                  const isSelected = bulkSelectedIds.has(member.memberId);
                  const statusColor = !member.declarationId ? "bg-gray-100 text-gray-600" :
                    member.declarationStatus === "pending_leader_approval" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                    member.declarationStatus === "ready_for_hr" || member.declarationStatus === "approved_by_leaders" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                    member.declarationStatus === "rejected" ? "bg-red-50 text-red-700 border border-red-200" :
                    "bg-blue-50 text-blue-700 border border-blue-200";
                  const statusLabel = !member.declarationId ? "Pending" :
                    member.declarationStatus === "pending_leader_approval" ? "Awaiting Approval" :
                    member.declarationStatus === "ready_for_hr" || member.declarationStatus === "approved_by_leaders" ? "Declared" :
                    member.declarationStatus === "rejected" ? "Rejected" :
                    member.declarationStatus.replace(/_/g, " ");

                  return (
                    <div key={member.memberId}
                      className={cn("px-4 py-3 flex items-center gap-3 hover:bg-gray-50/50 transition-colors cursor-pointer", isSelected && "bg-[#009A44]/[0.03]")}
                      onClick={() => member.canDeclare && toggleBulkMember(member.memberId)}>
                      <input type="checkbox" checked={isSelected}
                        onChange={() => toggleBulkMember(member.memberId)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={!member.canDeclare}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-[#009A44] focus:ring-[#009A44] disabled:opacity-40" />
                      <div className={cn("flex h-9 w-9 items-center justify-center rounded-full shrink-0 text-[11px] font-bold",
                        isSelected ? "bg-[#009A44] text-white" : "bg-gray-100 text-gray-600"
                      )}>
                        {getInitials(member.memberName?.split(" ")[0], member.memberName?.split(" ").slice(1).join(" "))}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-[12px] font-semibold text-gray-900 truncate">{member.memberName}</p>
                          <span className="text-[10px] text-gray-400 font-mono">{member.memberCode}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-gray-500">Shares: {member.totalShares}</span>
                          {member.declaredAmount > 0 && <><span className="text-[10px] text-gray-400">|</span><span className="text-[10px] font-medium text-[#009A44]">{formatCurrency(member.declaredAmount)}</span></>}
                        </div>
                      </div>
                      <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium shrink-0", statusColor)}>{statusLabel}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {membersError && !membersLoading && (
              <div className="py-16 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-red-50"><AlertTriangle className="h-6 w-6 text-red-400" /></div>
                <p className="text-[13px] font-medium text-gray-900">Failed to load members</p>
                <p className="text-[11px] text-gray-500 mt-0.5 mb-3">Check your connection and try again</p>
                <button onClick={() => refetchMembers()} className="inline-flex items-center gap-1.5 rounded-lg bg-[#009A44] px-3 py-1.5 text-[11px] font-medium text-white hover:bg-[#007a35] transition-colors">
                  <RefreshCw className="h-3 w-3" /> Retry
                </button>
              </div>
            )}

            {!membersLoading && !membersError && members.length === 0 && (
              <div className="py-16 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100"><Users className="h-6 w-6 text-gray-300" /></div>
                <p className="text-[13px] font-medium text-gray-700">No active members found</p>
                <p className="text-[11px] text-gray-400 mt-0.5 mb-3">{searchQuery ? "Try a different search term" : "No members available for this period"}</p>
                <button onClick={() => refetchMembers()} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                  <RefreshCw className="h-3 w-3" /> Refresh
                </button>
              </div>
            )}

            {hasMore && !membersLoading && (
              <div className="px-4 py-3 border-t border-gray-100 text-center">
                <button onClick={() => fetchNextPage()} className="text-[12px] font-medium text-[#009A44] hover:text-[#007a35] transition-colors">Load More...</button>
              </div>
            )}
          </Card>
        </div>
        )}
      </div>

      {/* ════════════ STICKY FOOTER ════════════ */}
      <div className="sticky bottom-0 z-40 border-t border-gray-200/80 bg-white/95 backdrop-blur-md">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between h-auto sm:h-[72px] gap-3 sm:gap-0 py-3 sm:py-0">
            {mode === "single" ? (
              <>
                <Button variant="outline" onClick={clearSelection}
                  className="order-2 sm:order-1 border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800 h-10 sm:h-[40px] px-5 text-[12px] font-medium rounded-xl">
                  Cancel
                </Button>
                <div className="order-1 sm:order-2 flex flex-col items-end gap-1.5">
                  {!canSubmit && totalPayment > 0 && (
                    <p className="text-[10px] text-amber-500 pr-1">
                      {missingReference ? `Reference # required for ${paymentMethod}` : isBelowMinimum ? `Min: ${formatCurrency(minimumAmount)}` : ""}
                    </p>
                  )}
                  <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={() => toast.info("Draft saved locally")}
                      disabled={!selectedMemberId}
                      className="flex-1 sm:flex-none border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800 h-10 sm:h-[40px] px-5 text-[12px] font-medium rounded-xl disabled:opacity-40 disabled:cursor-not-allowed">
                      <Save className="h-3.5 w-3.5 mr-1.5" /> Save Draft
                    </Button>
                    <Button onClick={() => setShowPreviewModal(true)}
                      disabled={!canSubmit || submitMutation.isLoading}
                      className="flex-1 sm:flex-none bg-[#009A44] hover:bg-[#007a35] text-white h-10 sm:h-[40px] px-6 text-[12px] font-semibold rounded-xl shadow-sm sm:min-w-[170px] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <span className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Preview Declaration</span>
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setBulkSelectedIds(new Set()); setBulkAmount(""); }}
                  className="order-2 sm:order-1 border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800 h-10 sm:h-[40px] px-5 text-[12px] font-medium rounded-xl">
                  Cancel
                </Button>
                <div className="order-1 sm:order-2 flex items-center gap-3">
                  <span className="text-[11px] text-gray-400 hidden sm:inline tabular-nums">
                    {bulkSelectedIds.size > 0 ? `${bulkSelectedIds.size} selected` : ""}
                  </span>
                  <Button disabled={bulkSelectedIds.size === 0} onClick={() => setShowBulkModal(true)}
                    className="flex-1 sm:flex-none bg-[#009A44] hover:bg-[#007a35] text-white h-10 sm:h-[40px] px-6 text-[12px] font-semibold rounded-xl shadow-sm sm:min-w-[180px] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    <span className="flex items-center gap-1.5"><Send className="h-3.5 w-3.5" />Declare{bulkSelectedIds.size > 0 ? ` (${bulkSelectedIds.size})` : ""}</span>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ════════════ BULK CONFIRMATION MODAL ════════════ */}
      {showBulkModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-semibold text-gray-900">Bulk Declaration</h3>
                <button onClick={() => { setShowBulkModal(false); setBulkAmount(""); }}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-[12px] text-gray-500 mt-1">
                Declare shares for {bulkSelectedIds.size} member{bulkSelectedIds.size !== 1 ? "s" : ""} in {months[selectedMonth - 1]} {selectedYear}.
              </p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <Label className="text-[12px] font-medium text-gray-700">Declaration Amount (FRW) *</Label>
                <Input type="number" value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)}
                  placeholder="Enter amount for all selected members" className="h-10 mt-1.5 text-[13px] rounded-lg" />
                <p className="mt-1 text-[10px] text-gray-400">This amount will be applied to all {bulkSelectedIds.size} selected member{bulkSelectedIds.size !== 1 ? "s" : ""}.</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-4 py-3 flex items-center justify-between">
                <span className="text-[12px] text-gray-600">Total Declarations</span>
                <span className="text-[15px] font-bold text-[#009A44]">
                  {bulkAmount ? formatCurrency(Number(bulkAmount) * bulkSelectedIds.size) : "—"}
                </span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowBulkModal(false); setBulkAmount(""); }}
                className="border-gray-300 h-9 px-4 text-[12px] font-medium rounded-lg">
                Cancel
              </Button>
              <Button onClick={() => bulkSubmitMutation.mutate()}
                disabled={bulkSubmitMutation.isLoading || !bulkAmount || Number(bulkAmount) <= 0}
                className="bg-[#009A44] hover:bg-[#007a35] text-white h-9 px-5 text-[12px] font-semibold rounded-lg shadow-sm disabled:opacity-50">
                {bulkSubmitMutation.isLoading ? (
                  <span className="flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Submitting...</span>
                ) : (
                  <span className="flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Confirm &amp; Submit</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════ PENDING DECLARATION DIALOG ════════════ */}
      {showPendingDialog && ws?.existingDeclaration && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-semibold text-gray-900">Existing Pending Declaration Found</h3>
                <button onClick={() => { setShowPendingDialog(false); clearSelection(); }}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-[13px] text-gray-600 leading-relaxed">
                This member already has a pending declaration of{" "}
                <span className="font-semibold text-gray-900">{formatCurrency(ws.existingDeclaration.declaredAmount)}</span>{" "}
                with approval progress{" "}
                <span className="font-semibold text-gray-900">{ws.existingDeclaration.approvalCount}/{ws.existingDeclaration.requiredApprovals}</span>.
              </p>
              <p className="text-[12px] text-gray-500">
                Do you want to add this new amount to the existing declaration?
              </p>
              <div className="rounded-lg bg-gray-50 px-4 py-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-500">Existing Amount</span>
                  <span className="text-[12px] font-semibold text-gray-900">{formatCurrency(ws.existingDeclaration.declaredAmount)}</span>
                </div>
                {contributionNum > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-500">New Contribution</span>
                    <span className="text-[12px] font-semibold text-emerald-600">+ {formatCurrency(contributionNum)}</span>
                  </div>
                )}
                {contributionNum > 0 && (
                  <div className="flex items-center justify-between border-t border-gray-200 pt-1.5">
                    <span className="text-[11px] font-medium text-gray-700">Total After Add</span>
                    <span className="text-[13px] font-bold text-[#009A44]">{formatCurrency(ws.existingDeclaration.declaredAmount + contributionNum)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowPendingDialog(false); clearSelection(); }}
                className="border-gray-300 h-9 px-4 text-[12px] font-medium rounded-lg">
                Cancel
              </Button>
              <Button onClick={() => { setDeclarationMode("add_to_existing"); setPendingDeclarationId(ws.existingDeclaration!.id); setShowPendingDialog(false); }}
                className="bg-[#009A44] hover:bg-[#007a35] text-white h-9 px-5 text-[12px] font-semibold rounded-lg shadow-sm">
                <span className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" /> Add to Existing Declaration</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════ PREVIEW MODAL ════════════ */}
      {showPreviewModal && ws && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-semibold text-gray-900">Declaration Preview</h3>
                <button onClick={() => setShowPreviewModal(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-[12px] text-gray-500 mt-1">Review your declaration before submitting.</p>
            </div>

            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              {/* Member Info */}
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Member Information</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-[10px] text-gray-400">Name</span>
                    <p className="text-[12px] font-medium text-gray-900">{ws.member.fullName}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400">Code</span>
                    <p className="text-[12px] font-medium text-gray-900">{ws.member.memberCode}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400">Phone</span>
                    <p className="text-[12px] font-medium text-gray-900">{ws.member.phone || "—"}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400">Month/Year</span>
                    <p className="text-[12px] font-medium text-gray-900">{months[selectedMonth - 1]} {selectedYear}</p>
                  </div>
                </div>
              </div>

              {/* Declaration Details */}
              <div className="rounded-xl border border-gray-200 px-4 py-3 space-y-2.5">
                <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Declaration Details</h4>

                {declarationMode === "add_to_existing" && ws.existingDeclaration && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-[11px] text-gray-500">Existing Declaration</span>
                    <span className="text-[12px] font-semibold text-gray-700">{formatCurrency(ws.existingDeclaration.declaredAmount)}</span>
                  </div>
                )}

                {contributionNum > 0 && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-[11px] text-gray-500">
                      {declarationMode === "add_to_existing" ? "New Contribution" : "Contribution"}
                    </span>
                    <span className="text-[12px] font-semibold text-emerald-600">{formatCurrency(contributionNum)}</span>
                  </div>
                )}

                {declarationMode === "add_to_existing" && contributionNum > 0 && (
                  <div className="flex items-center justify-between py-1 border-t border-gray-100 pt-2">
                    <span className="text-[11px] font-medium text-gray-700">Total Declaration Amount</span>
                    <span className="text-[13px] font-bold text-[#009A44]">
                      {formatCurrency((ws.existingDeclaration?.declaredAmount || 0) + contributionNum)}
                    </span>
                  </div>
                )}

                {contributionNum > 0 && declarationMode !== "add_to_existing" && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-[11px] text-gray-500">Shares to Purchase</span>
                    <span className="text-[12px] font-semibold text-[#009A44]">{sharesFromContribution}</span>
                  </div>
                )}

                {declarationMode === "add_to_existing" && contributionNum > 0 && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-[11px] text-gray-500">Additional Shares</span>
                    <span className="text-[12px] font-semibold text-[#009A44]">{sharesFromContribution}</span>
                  </div>
                )}

                {loanRepaymentNum > 0 && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-[11px] text-gray-500">Loan Repayment</span>
                    <span className="text-[12px] font-semibold text-blue-600">{formatCurrency(loanRepaymentNum)}</span>
                  </div>
                )}

                {fineNum > 0 && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-[11px] text-gray-500">Fine Payment</span>
                    <span className="text-[12px] font-semibold text-amber-600">{formatCurrency(fineNum)}</span>
                  </div>
                )}

                <div className="flex items-center justify-between py-1.5 border-t border-gray-200">
                  <span className="text-[12px] font-semibold text-gray-800">Total Payment</span>
                  <span className="text-[14px] font-bold text-gray-900">{formatCurrency(totalPayment)}</span>
                </div>
              </div>

              {/* Remaining Balances */}
              {(ws.loanSummary || ws.fines.pendingFines > 0) && (
                <div className="rounded-xl bg-gray-50 px-4 py-3 space-y-1.5">
                  <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Remaining Balances</h4>
                  {ws.loanSummary && (
                    <div className="flex items-center justify-between py-0.5">
                      <span className="text-[11px] text-gray-500">Loan Balance</span>
                      <span className={cn("text-[12px] font-semibold", (ws.loanSummary.loanBalance - loanRepaymentNum) > 0 ? "text-red-600" : "text-emerald-600")}>
                        {formatCurrency(Math.max(0, ws.loanSummary.loanBalance - loanRepaymentNum))}
                      </span>
                    </div>
                  )}
                  {ws.fines.pendingFines > 0 && (
                    <div className="flex items-center justify-between py-0.5">
                      <span className="text-[11px] text-gray-500">Pending Fines</span>
                      <span className={cn("text-[12px] font-semibold", (ws.fines.pendingFines - fineNum) > 0 ? "text-red-600" : "text-emerald-600")}>
                        {formatCurrency(Math.max(0, ws.fines.pendingFines - fineNum))}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Method */}
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Payment</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-[10px] text-gray-400">Method</span>
                    <p className="text-[12px] font-medium text-gray-900">{paymentMethod}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400">Date</span>
                    <p className="text-[12px] font-medium text-gray-900">{paymentDate}</p>
                  </div>
                  {referenceNumber && (
                    <div>
                      <span className="text-[10px] text-gray-400">Reference</span>
                      <p className="text-[12px] font-medium text-gray-900">{referenceNumber}</p>
                    </div>
                  )}
                  {notes && (
                    <div className="col-span-2">
                      <span className="text-[10px] text-gray-400">Notes</span>
                      <p className="text-[12px] font-medium text-gray-900">{notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Approval Route */}
              <div className="rounded-lg bg-[#009A44]/5 border border-[#009A44]/10 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#009A44]" />
                  <span className="text-[12px] text-gray-600">Approval Route</span>
                </div>
                <span className="inline-flex rounded-full bg-[#009A44]/10 px-2.5 py-0.5 text-[10px] font-semibold text-[#009A44]">
                  Pending Leader Approval — 3 approvals required
                </span>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2 shrink-0">
              <Button variant="outline" onClick={() => setShowPreviewModal(false)}
                className="border-gray-300 h-9 px-4 text-[12px] font-medium rounded-lg">
                Back / Edit
              </Button>
              <Button onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isLoading}
                className="bg-[#009A44] hover:bg-[#007a35] text-white h-9 px-5 text-[12px] font-semibold rounded-lg shadow-sm disabled:opacity-50">
                {submitMutation.isLoading ? (
                  <span className="flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Submitting...</span>
                ) : (
                  <span className="flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Confirm &amp; Submit for Approval</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAddMemberModal && <AddMemberForm />}
    </div>
  );
}
